import { ReactNode } from 'react';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import {
  createResourceLinkExtension,
  extractLastNameComponent,
} from '@spotify/confidence-core-react';

import { confidenceAdminPlugin } from '../plugin';
import { entityRouteRef, factTableRouteRef } from '../routes';

interface EntityResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const isEntityResourceName = (name: string) =>
  name.startsWith('entities/') && !name.slice('entities/'.length).includes('/');

const EntityResourceLink = ({
  name,
  text,
  className,
}: EntityResourceLinkProps) => {
  const routeRef = useRouteRef(entityRouteRef);
  const id = extractLastNameComponent(name);

  if (!routeRef || !id) {
    return <>{text}</>;
  }

  const to = routeRef({ id } as Parameters<NonNullable<typeof routeRef>>[0]);

  return (
    <Link to={to} className={className}>
      {text}
    </Link>
  );
};

interface FactTableResourceLinkProps {
  name: string;
  text: ReactNode;
  className?: string;
}

const isFactTableResourceName = (name: string) =>
  name.startsWith('factTables/') &&
  !name.slice('factTables/'.length).includes('/');

const FactTableResourceLink = ({
  name,
  text,
  className,
}: FactTableResourceLinkProps) => {
  const routeRef = useRouteRef(factTableRouteRef);
  const id = extractLastNameComponent(name);

  if (!routeRef || !id) {
    return <>{text}</>;
  }

  const to = routeRef({ id } as Parameters<NonNullable<typeof routeRef>>[0]);

  return (
    <Link to={to} className={className}>
      {text}
    </Link>
  );
};

export const EntityResourceLinkExtension = confidenceAdminPlugin.provide(
  createResourceLinkExtension({
    id: 'entity-resource-link',
    predicate: ({ name }) => isEntityResourceName(name),
    render: ({ name, text, className }) => (
      <EntityResourceLink name={name} text={text} className={className} />
    ),
  }),
);

export const FactTableResourceLinkExtension = confidenceAdminPlugin.provide(
  createResourceLinkExtension({
    id: 'fact-table-resource-link',
    predicate: ({ name }) => isFactTableResourceName(name),
    render: ({ name, text, className }) => (
      <FactTableResourceLink name={name} text={text} className={className} />
    ),
  }),
);
